import AddIcon from "@mui/icons-material/Add";
import CompressIcon from "@mui/icons-material/CompressOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import FlightIcon from "@mui/icons-material/Flight";
import UploadIcon from "@mui/icons-material/Upload";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LabelIcon from "@mui/icons-material/Label";
import LayersIcon from "@mui/icons-material/Layers";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import MarginIcon from "@mui/icons-material/Margin";
import CompassIcon from "@mui/icons-material/Navigation";
import NearMeIcon from "@mui/icons-material/NearMe";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RouteIcon from "@mui/icons-material/Route";
import StraightenIcon from "@mui/icons-material/Straighten";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import TextureIcon from "@mui/icons-material/Texture";
import TitleIcon from "@mui/icons-material/Title";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useHangarsState } from "../../../../../containers/HangarsStateContainer";
import { useMeasuringToolState } from "../../../../../containers/MeasuringToolContainer";
import { CalibrationPoint } from "../../../../../types";
import { uuidv4 } from "../../../../../utils";
import { RampIcon } from "../../../../../widgets/Icons/RampIcon";
import { SplitButton } from "../../../../../widgets/SplitButton";
import { CalibrationPointDialog } from "../CalibrationPointDialog";
import { useImageUpload } from "../hooks/useImageUpload";
import { useSnackbar } from "../../../../../containers/SnackbarContainer";
import { compressImageServerSide } from "../../../../../services/ImageCompressionService";
import { CompressionResult } from "../../../../../types/compression";
import { CalibrationPointsList } from "../CalibrationPointsList";
import { GISImportDialog } from "../GISImport/GISImportDialog";
import { EgressCalculationDialog } from "../RampCanvas/EgressCalculationDialog";
import {
  getDefaultSizeCategory,
  Shape,
  SIZE_CATEGORY_OPTIONS,
} from "../RampCanvas/RampCanvas";
import {
  calculateBlockedSpots,
  calculateSpotNames,
  EgressCalculationResult,
} from "../RampCanvas/spotHelpers";
import {
  useCalibrationState,
  useLocalRampState,
  useRampEditorState,
} from "../RampEditorPresenter";
import { ReferenceAircraftDialog } from "../ReferenceAircraftDialog";
import { useReferenceAircraftState } from "../stores/useReferenceAircraftState";
import { ShapeElements } from "./ShapeElements";

export interface Tool {
  name: string;
  icon: JSX.Element;
  shortcut?: string;
  onClick: () => void;
}

type Props = {
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
};

export const Toolbar: React.FC<Props> = ({ selectedTool, setSelectedTool }) => {
  const { t } = useTranslation();
  const {
    showReferenceImage,
    setShowReferenceImage,
    showMeasurements,
    setShowMeasurements,
    showGuideLines,
    setShowGuideLines,
    showCalibrationPoints,
    setShowCalibrationPoints,
    setHighlightedCalibrationPoint,
  } = useRampEditorState();
  const { ramp, setRamp, setActiveShapes } = useLocalRampState();
  const { isAddingCalibrationPoint, setIsAddingCalibrationPoint } =
    useCalibrationState();
  const { hangars } = useHangarsState();
  const measuringToolState = useMeasuringToolState();
  const [activeTab, setActiveTab] = React.useState<string>("Tools");
  const [calibrationDialog, setCalibrationDialog] = React.useState<{
    open: boolean;
    x?: number;
    y?: number;
    point?: CalibrationPoint;
  }>({ open: false });
  const [gisImportDialogOpen, setGisImportDialogOpen] =
    React.useState<boolean>(false);
  const [egressDialogOpen, setEgressDialogOpen] =
    React.useState<boolean>(false);
  const [egressResult, setEgressResult] =
    React.useState<EgressCalculationResult | null>(null);
  const [referenceAircraftDialogOpen, setReferenceAircraftDialogOpen] =
    React.useState<boolean>(false);
  const { aircraft: referenceAircraft } = useReferenceAircraftState();

  // Image upload hook
  const { handleUpload: handleImageUpload, isUploading: isUploadingImage } =
    useImageUpload(ramp, setRamp);

  // Snackbar for notifications
  const snackbar = useSnackbar();

  // Loading state for compress button
  const [isCompressing, setIsCompressing] = React.useState(false);

  // Compress existing image by calling backend directly
  const handleCompressImage = async () => {
    if (!ramp.reference_image || isUploadingImage || isCompressing) return;

    setIsCompressing(true);

    try {
      // Convert Base64 data URI to Blob
      const response = await fetch(ramp.reference_image);
      const blob = await response.blob();
      const file = new File([blob], "image.png", { type: blob.type });

      // Call compression service
      const result: CompressionResult = await compressImageServerSide(file);

      // Update ramp with compressed image
      setRamp({
        ...ramp,
        reference_image: result.data,
      });

      // Calculate KB saved for display
      const savedKilobytes = (
        (result.originalSize - result.compressedSize) /
        1024
      ).toFixed(0);

      // Show success notification with savings
      snackbar.notify({
        text: t('settings.rampsSettings.rampEditor.toolbar.snackbar.imageSaved', {
          kb: savedKilobytes,
          percent: result.savings
        }),
        severity: "success",
        duration: 4000,
      });
    } catch (error) {
      console.error("Failed to compress image:", error);
      snackbar.notify({
        text: t('settings.rampsSettings.rampEditor.toolbar.snackbar.compressionFailed'),
        severity: "error",
      });
    } finally {
      setIsCompressing(false);
    }
  };

  // Options panel collapse state with localStorage persistence
  const [optionsExpanded, setOptionsExpanded] = React.useState<boolean>(() => {
    const saved = localStorage.getItem("ramp-editor-options-expanded");
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Calibration Points panel collapse state with localStorage persistence
  const [calibrationPointsExpanded, setCalibrationPointsExpanded] =
    React.useState<boolean>(() => {
      const saved = localStorage.getItem(
        "ramp-editor-calibration-points-expanded"
      );
      return saved !== null ? JSON.parse(saved) : true;
    });

  // Save options expanded state to localStorage
  React.useEffect(() => {
    localStorage.setItem(
      "ramp-editor-options-expanded",
      JSON.stringify(optionsExpanded)
    );
  }, [optionsExpanded]);

  // Save calibration points expanded state to localStorage
  React.useEffect(() => {
    localStorage.setItem(
      "ramp-editor-calibration-points-expanded",
      JSON.stringify(calibrationPointsExpanded)
    );
  }, [calibrationPointsExpanded]);

  const setShapes = React.useCallback(
    (shapesOrUpdater: Shape[] | ((prevShapes: Shape[]) => Shape[])) => {
      setRamp((currentRamp) => {
        if (!currentRamp) return currentRamp;

        return {
          ...currentRamp,
          shapes:
            typeof shapesOrUpdater === "function"
              ? shapesOrUpdater(currentRamp.shapes || [])
              : shapesOrUpdater,
        };
      });
    },
    [setRamp]
  );

  const handleAddCalibrationPoint = (x: number, y: number) => {
    setCalibrationDialog({ open: true, x, y });
  };

  const handleSaveCalibrationPoint = (point: CalibrationPoint) => {
    const existingIndex = ramp.calibration_points?.findIndex(
      (p) => p.id === point.id
    );
    let updatedPoints = ramp.calibration_points || [];

    if (existingIndex !== undefined && existingIndex >= 0) {
      updatedPoints[existingIndex] = point;
    } else {
      updatedPoints = [...updatedPoints, point];
    }

    setRamp({
      ...ramp,
      calibration_points: updatedPoints,
    });
  };

  const handleDeleteCalibrationPoint = (pointId: string) => {
    setRamp({
      ...ramp,
      calibration_points:
        ramp.calibration_points?.filter((p) => p.id !== pointId) || [],
    });
  };

  const handleImportGISShapes = (newShapes: Shape[]) => {
    setRamp((currentRamp) => ({
      ...currentRamp,
      shapes: [...currentRamp.shapes, ...newShapes],
    }));
    setGisImportDialogOpen(false);
  };

  // Guard to ensure ramp is properly loaded
  const isRampValid = () => {
    if (!ramp || !ramp.id || !ramp.fbo_id) {
      console.error("Ramp not properly loaded - cannot create shape");
      return false;
    }
    return true;
  };

  // Expose the add calibration point function globally for use by canvas
  React.useEffect(() => {
    if (isAddingCalibrationPoint) {
      (window as any).addCalibrationPoint = handleAddCalibrationPoint;
    } else {
      delete (window as any).addCalibrationPoint;
    }
  }, [isAddingCalibrationPoint]);

  const startingPoint = {
    x: (ramp?.width ?? 0) / 2,
    y: (ramp?.depth ?? 0) / 2,
  };

  const DEFAULT_SPOT_COLOR = "#FFFFFF";

  const createSpotShape = (size: number) => {
    if (!isRampValid()) return;
    setActiveShapes([]);

    // Get label and size category for this spot
    const sizeOption = SIZE_CATEGORY_OPTIONS.find(
      (opt) => opt.maxFeet === size
    );
    const sizeCategory = getDefaultSizeCategory(size);

    setShapes([
      {
        id: uuidv4(),
        fbo_id: ramp.fbo_id,
        ramp_id: ramp.id,
        tags: ["spot"],
        type: "Spot",
        locked: false,
        points: [
          { x: startingPoint.x - size / 2, y: startingPoint.y - size / 2 },
          { x: startingPoint.x + size / 2, y: startingPoint.y - size / 2 },
          { x: startingPoint.x + size / 2, y: startingPoint.y + size / 2 },
          { x: startingPoint.x - size / 2, y: startingPoint.y + size / 2 },
        ],
        stroke_style: DEFAULT_SPOT_COLOR,
        line_style: "dashed",
        line_width: 3,
        fill_style: null,
        font_size: 14,
        reference_image_height: null,
        text: sizeOption?.label || size.toString(),
        reference_points: [
          {
            name: "north",
            validOrientations: ["nose_to_line", "tail_to_line"],
          },
        ],
        metadata: {
          allowedSizes: [sizeCategory],
          egressLanes: [],
        },
      },
      ...ramp.shapes,
    ]);
    setSelectedTool("Select");
    measuringToolState.setActive(false);
  };

  const tools: Tool[] = [
    {
      name: "Select",
      icon: <NearMeIcon />,
      onClick: () => {
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Area",
      icon: <TextureIcon />,
      shortcut: "A",
      onClick: () => {
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Zone",
      icon: <LayersIcon />,
      onClick: () => {
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Line",
      icon: <LinearScaleIcon />,
      shortcut: "L",
      onClick: () => {
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Tie Down T",
      icon: <TitleIcon />,
      shortcut: "Tie Down T",
      onClick: () => {
        if (!isRampValid()) return;
        setActiveShapes([]);
        setShapes([
          {
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            type: "Tie Down T",
            locked: false,
            points: [
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 100 },
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 100 },
            ],
            stroke_style: "black",
          },
          ...ramp.shapes,
        ]);
        setSelectedTool("Select");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Double T",
      icon: (
        <Box
          sx={{
            position: "relative",

            width: 20, // Adjust size as needed
            height: 20,
          }}
        >
          {/* Right-side-up TitleIcon */}
          <TitleIcon
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              fontSize: 20,
              color: "black",
            }}
          />
          {/* Upside-down TitleIcon */}
          <TitleIcon
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              fontSize: 20,
              color: "black", // Different color for distinction
              transform: "rotate(180deg)",
            }}
          />
        </Box>
      ),
      shortcut: "Double T",
      onClick: () => {
        if (!isRampValid()) return;
        setActiveShapes([]);
        setShapes([
          {
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            type: "Double T",
            locked: false,
            points: [
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 100 },
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 100 },
            ],
            stroke_style: "black",
          },
          ...ramp.shapes,
        ]);
        setSelectedTool("Select");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Circle",
      icon: <RadioButtonUncheckedIcon />,
      onClick: () => {
        if (!isRampValid()) return;
        setActiveShapes([]);
        setShapes([
          {
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            type: "Circle",
            locked: false,
            points: [
              { x: startingPoint.x + 0 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 100 },
              { x: startingPoint.x + 0 - 50, y: startingPoint.y + 100 },
            ],
            stroke_style: "black",
            line_style: "solid",
            line_width: 3,
            fill_style: null,
            font_size: null,
            reference_image_height: null,
            text: null,
          },
          ...ramp.shapes,
        ]);
        setSelectedTool("Select");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Helipad",
      icon: (
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24, // Adjust size as needed
            height: 24,
          }}
        >
          <RadioButtonUncheckedIcon />
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              color: "black",
              fontWeight: "bold",
            }}
          >
            H
          </Typography>
        </Box>
      ),
      onClick: () => {
        if (!isRampValid()) return;
        setActiveShapes([]);
        setShapes([
          {
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            type: "Helipad",
            locked: false,
            points: [
              { x: startingPoint.x + 0 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 100 },
              { x: startingPoint.x + 0 - 50, y: startingPoint.y + 100 },
            ],
            stroke_style: "#f9de55",
            line_style: "solid",
            line_width: 3,
            fill_style: null,
            font_size: 50,
            reference_image_height: null,
            text: "H",
          },
          ...ramp.shapes,
        ]);
        setSelectedTool("Select");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Spot",
      icon: <MarginIcon />,
      onClick: () => {
        // This is a placeholder - actual rendering uses SplitButton
      },
    },
    {
      name: "Text",
      icon: <TextFieldsIcon />,
      onClick: () => {
        setActiveShapes([]);
        console.log("Text");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Measuring",
      icon: <StraightenIcon />,
      onClick: () => {
        setActiveShapes([]);
        measuringToolState.setActive(true);
      },
    },
    {
      name: "Compass Rose",
      icon: <CompassIcon />,
      onClick: () => {
        if (!isRampValid()) return;
        setActiveShapes([]);
        // if there is already a compass rose, don't add another one
        if (ramp.shapes.find((s) => s.type === "Compass Rose")) {
          console.log("Already a compass rose in this ramp");
          return;
        }
        setShapes([
          {
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            type: "Compass Rose",
            locked: false,
            points: [
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 100 },
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 100 },
            ],
            stroke_style: "#000000",
          },
          ...ramp.shapes,
        ]);
        setSelectedTool("Select");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Reference Aircraft",
      icon: <FlightIcon />,
      onClick: () => {
        setReferenceAircraftDialogOpen(true);
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
  ];

  const bulkTools: Tool[] = [
    {
      name: "Import GIS Shapes",
      icon: <RampIcon />,
      onClick: () => {
        setGisImportDialogOpen(true);
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Calculate Egress",
      icon: <RouteIcon />,
      onClick: () => {
        if (!isRampValid()) return;
        const result = calculateBlockedSpots(ramp.shapes);
        setEgressResult(result);
        setShapes(result.shapes);
        setEgressDialogOpen(true);
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Auto-Name Spots",
      icon: <LabelIcon />,
      onClick: () => {
        if (!isRampValid()) return;
        const updatedShapes = calculateSpotNames(ramp.shapes);
        setShapes(updatedShapes);
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
  ];

  return (
    <Stack direction="column" alignItems="flex-start" spacing={1}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
        }}
        onClick={() => setOptionsExpanded(!optionsExpanded)}
      >
        <Typography variant="subtitle2">{t('settings.rampsSettings.rampEditor.toolbar.sections.options')}</Typography>
        {optionsExpanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Box>

      <Collapse in={optionsExpanded} sx={{ width: "100%" }}>
        <Stack direction="column" alignItems="flex-start" spacing={1}>
          <Stack direction="row" spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  color="primary"
                  checked={showReferenceImage}
                  onChange={() => {
                    setShowReferenceImage(!showReferenceImage);
                  }}
                />
              }
              label={t('settings.rampsSettings.rampEditor.toolbar.options.image')}
              labelPlacement="start"
            />
            <Tooltip title={t('settings.rampsSettings.rampEditor.toolbar.tooltips.downloadImage')}>
              <IconButton
                size="small"
                onClick={() => {
                  // download the image (ramp.reference_image)
                  const a = document.createElement("a");
                  a.href = ramp.reference_image;
                  a.download = `image-${ramp.name}.png`;
                  a.click();
                }}
                sx={{ p: 0 }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('settings.rampsSettings.rampEditor.toolbar.tooltips.uploadImage')}>
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  size="small"
                  component="label"
                  disabled={isUploadingImage}
                  sx={{ p: 0 }}
                >
                  {isUploadingImage ? (
                    <CircularProgress size={20} />
                  ) : (
                    <UploadIcon />
                  )}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title={t('settings.rampsSettings.rampEditor.toolbar.tooltips.compressImage')}>
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  size="small"
                  onClick={handleCompressImage}
                  disabled={isUploadingImage || isCompressing || !ramp.reference_image}
                  sx={{ p: 0 }}
                >
                  {isCompressing ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CompressIcon />
                  )}
                </IconButton>
              </Box>
            </Tooltip>
          </Stack>
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={showMeasurements}
                onChange={() => setShowMeasurements(!showMeasurements)}
              />
            }
            label={t('settings.rampsSettings.rampEditor.toolbar.options.measurements')}
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={ramp?.version === "v2"}
                onChange={() =>
                  setRamp({
                    ...ramp,
                    version: ramp?.version === "v2" ? "v1" : "v2",
                  })
                }
              />
            }
            label={t('settings.rampsSettings.rampEditor.toolbar.options.rampV2')}
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={ramp?.autoparking_enabled ?? false}
                onChange={() =>
                  setRamp({
                    ...ramp,
                    autoparking_enabled: !ramp?.autoparking_enabled,
                  })
                }
              />
            }
            label={t('settings.rampsSettings.rampEditor.toolbar.options.autoParking')}
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={showGuideLines}
                onChange={() => setShowGuideLines(!showGuideLines)}
              />
            }
            label={t('settings.rampsSettings.rampEditor.toolbar.options.guideLines')}
            labelPlacement="start"
          />
        </Stack>
      </Collapse>

      {/* Calibration Points Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
        }}
        onClick={() => setCalibrationPointsExpanded(!calibrationPointsExpanded)}
      >
        <Typography variant="subtitle2">{t('settings.rampsSettings.rampEditor.toolbar.sections.calibrationPoints')}</Typography>
        {calibrationPointsExpanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Box>

      <Collapse in={calibrationPointsExpanded} sx={{ width: "100%" }}>
        <Stack direction="column" spacing={1} alignItems="flex-start">
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={showCalibrationPoints}
                onChange={() =>
                  setShowCalibrationPoints(!showCalibrationPoints)
                }
              />
            }
            label={t('settings.rampsSettings.rampEditor.toolbar.options.showPoints')}
            labelPlacement="start"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "0.875rem",
              },
            }}
          />

          <Button
            size="small"
            variant={isAddingCalibrationPoint ? "contained" : "outlined"}
            startIcon={<GpsFixedIcon />}
            onClick={() => {
              setIsAddingCalibrationPoint(!isAddingCalibrationPoint);
              setSelectedTool(
                isAddingCalibrationPoint ? "Select" : "CalibrationPoint"
              );
            }}
            color={isAddingCalibrationPoint ? "primary" : "inherit"}
          >
            {isAddingCalibrationPoint ? t('settings.rampsSettings.rampEditor.toolbar.buttons.finishedAdding') : t('settings.rampsSettings.rampEditor.toolbar.buttons.addPoint')}
          </Button>

          <CalibrationPointsList
            calibrationPoints={ramp.calibration_points || []}
            onEdit={(point) => setCalibrationDialog({ open: true, point })}
            onDelete={handleDeleteCalibrationPoint}
            onHighlight={setHighlightedCalibrationPoint}
          />
        </Stack>
      </Collapse>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          width: "100%",
          minHeight: "auto",
          "& .MuiTabs-indicator": {
            height: 2,
          },
          "& .MuiTab-root": {
            minHeight: 32,
            minWidth: 0,
            padding: "6px 8px",
            fontSize: "0.75rem",
            textTransform: "none",
            flex: 1,
          },
        }}
      >
        <Tab label={t('settings.rampsSettings.rampEditor.toolbar.tabs.tools')} value="Tools" />
        <Tab label={t('settings.rampsSettings.rampEditor.toolbar.tabs.shapes')} value="Shapes" />
        <Tab label={t('settings.rampsSettings.rampEditor.toolbar.tabs.hangars')} value="Hangars" />
      </Tabs>
      {activeTab === "Tools" && (
        <Stack
          direction="column"
          alignItems="flex-start"
          spacing={1}
          width="100%"
        >
          {tools.map((tool) => {
            if (tool.name === "Spot") {
              return (
                <SplitButton
                  key={`tool-button-${tool.name}`}
                  startIcon={tool.icon}
                  variant="text"
                  color={selectedTool === tool.name ? "primary" : "inherit"}
                  size="medium"
                  defaultSelectedIndex={3}
                  options={[
                    {
                      label: t('settings.rampsSettings.rampEditor.toolbar.spotSizes.verySmall'),
                      onClick: () => {
                        createSpotShape(40);
                      },
                    },
                    {
                      label: t('settings.rampsSettings.rampEditor.toolbar.spotSizes.small'),
                      onClick: () => {
                        createSpotShape(60);
                      },
                    },
                    {
                      label: t('settings.rampsSettings.rampEditor.toolbar.spotSizes.midsize'),
                      onClick: () => {
                        createSpotShape(80);
                      },
                    },
                    {
                      label: t('settings.rampsSettings.rampEditor.toolbar.spotSizes.superMid'),
                      onClick: () => {
                        createSpotShape(100);
                      },
                    },
                    {
                      label: t('settings.rampsSettings.rampEditor.toolbar.spotSizes.large'),
                      onClick: () => {
                        createSpotShape(120);
                      },
                    },
                  ]}
                />
              );
            }
            // Reference Aircraft button shows "outlined" when aircraft is active
            if (tool.name === "Reference Aircraft") {
              return (
                <Button
                  key={`tool-button-${tool.name}`}
                  startIcon={tool.icon}
                  variant={referenceAircraft ? "outlined" : "text"}
                  color={referenceAircraft ? "primary" : "inherit"}
                  onClick={() => {
                    tool.onClick();
                  }}
                >
                  {referenceAircraft
                    ? `${referenceAircraft.make} ${referenceAircraft.model}`
                    : tool.name}
                </Button>
              );
            }
            return (
              <Button
                key={`tool-button-${tool.name}`}
                startIcon={tool.icon}
                variant={selectedTool === tool.name ? "contained" : "text"}
                onClick={() => {
                  setSelectedTool(tool.name);
                  tool.onClick();
                }}
              >
                {tool.name}
              </Button>
            );
          })}
          <Divider flexItem sx={{ my: 1 }} />
          <Typography variant="caption" sx={{ pl: 1, color: "text.secondary" }}>
            {t('settings.rampsSettings.rampEditor.toolbar.bulkOperations')}
          </Typography>
          {bulkTools.map((tool) => (
            <Button
              key={`bulk-tool-button-${tool.name}`}
              startIcon={tool.icon}
              variant="text"
              onClick={tool.onClick}
              sx={{ justifyContent: "flex-start" }}
            >
              {tool.name}
            </Button>
          ))}
        </Stack>
      )}
      {activeTab === "Shapes" && (
        <ShapeElements
          shapes={ramp?.shapes || []}
          setShapes={setShapes}
          tools={tools}
        />
      )}
      {activeTab === "Hangars" && (
        <Stack direction="column" alignItems="flex-start" width="100%">
          {hangars.length === 0 && (
            <Typography variant="subtitle2" align="center">
              {t('settings.rampsSettings.rampEditor.toolbar.noHangarsAvailable')}
            </Typography>
          )}
          {hangars.map((hangar) => (
            <Button
              key={`hangar-button-${hangar.id}`}
              variant="text"
              disabled={Boolean(
                ramp.shapes.find((s) => s.hangar_id === hangar.id)
              )}
              onClick={() => {
                if (!isRampValid()) return;
                console.log(`${hangar.width} ft x ${hangar.depth} ft`);
                setShapes([
                  {
                    id: uuidv4(),
                    fbo_id: ramp.fbo_id,
                    ramp_id: ramp.id,
                    hangar_id: hangar.id,
                    tags: ["hangar"],
                    type: "Area",
                    locked: false,
                    line_style: "solid",
                    line_width: 3,
                    fill_style: "#c8cfde50",
                    stroke_style: "#3e5d8c",
                    text: hangar.name,
                    font_size: 12,
                    reference_image_height: null,
                    points: [
                      {
                        x: startingPoint.x + 0 - hangar.width / 2,
                        y: startingPoint.y + 0,
                      },
                      {
                        x: startingPoint.x + hangar.width - hangar.width / 2,
                        y: startingPoint.y + 0,
                      },
                      {
                        x: startingPoint.x + hangar.width - hangar.width / 2,
                        y: startingPoint.y + hangar.depth,
                      },
                      {
                        x: startingPoint.x + 0 - hangar.width / 2,
                        y: startingPoint.y + hangar.depth,
                      },
                    ],
                  },
                  ...ramp.shapes,
                ]);
              }}
              startIcon={<AddIcon />}
            >
              {hangar.name}
            </Button>
          ))}
        </Stack>
      )}
      <Divider flexItem />

      <CalibrationPointDialog
        open={calibrationDialog.open}
        onClose={() => setCalibrationDialog({ open: false })}
        onSave={handleSaveCalibrationPoint}
        point={calibrationDialog.point}
        x={calibrationDialog.x || 0}
        y={calibrationDialog.y || 0}
        rampId={ramp.id}
      />

      <GISImportDialog
        open={gisImportDialogOpen}
        onClose={() => setGisImportDialogOpen(false)}
        onImportShapes={handleImportGISShapes}
        ramp={ramp}
      />

      <EgressCalculationDialog
        open={egressDialogOpen}
        onClose={() => setEgressDialogOpen(false)}
        result={egressResult}
      />

      <ReferenceAircraftDialog
        open={referenceAircraftDialogOpen}
        onClose={() => setReferenceAircraftDialogOpen(false)}
        rampWidth={ramp?.width ?? 0}
        rampDepth={ramp?.depth ?? 0}
      />
    </Stack>
  );
};
